import React from "react";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import OrganizerTimePillSelect from "../components/OrganizerTimePillSelect";
import AvisoSenasBloqueadas from "../components/AvisoSenasBloqueadas";
import { SPORT_LABELS } from "../../constants/courtDomain";
import {
  BIRTHDAY_HOURS_OPTIONS,
  DURATION_OPTIONS,
  MAX_PACKAGES,
  MAX_WINDOWS,
  formatDuration,
  newBirthdayPackage,
  newBirthdayWindow,
} from "./clubBirthdays";
import "./ClubBirthdaysTab.css";

/* ─────────────────────────────────────────
   CUMPLEAÑOS tab of the club form (task F1 of canchas-cumpleanos). Pure view over
   `clubForm.birthdays`: every change goes through `onChange(updater)`, and the club form saves it
   with the rest of the club (buildClubFormData). `errors` are the messages of validateBirthdays,
   shown next to the section they belong to once the organizer tried to save.
───────────────────────────────────────── */

// Monday first, the way the organizer reads a week; the value is the JS weekday (0 = Sunday).
const WEEKDAY_CHIPS = [
  { weekday: 1, short: "Lun", label: "Lunes" },
  { weekday: 2, short: "Mar", label: "Martes" },
  { weekday: 3, short: "Mié", label: "Miércoles" },
  { weekday: 4, short: "Jue", label: "Jueves" },
  { weekday: 5, short: "Vie", label: "Viernes" },
  { weekday: 6, short: "Sáb", label: "Sábado" },
  { weekday: 0, short: "Dom", label: "Domingo" },
];

const HOURS_OPTIONS = BIRTHDAY_HOURS_OPTIONS.map((h) => ({ value: h, label: `${h} h` }));

const QUESTIONS = [
  { key: "birthdayPerson", label: "Nombre del cumpleañero" },
  { key: "age", label: "Edad que cumple" },
  { key: "guests", label: "Cantidad de invitados" },
  { key: "comments", label: "Comentarios" },
];

const ErrorList = ({ messages }) =>
  messages && messages.length > 0 ? (
    <ul className="zr-bday__errors" role="alert">
      {messages.map((message, idx) => (
        <li key={`${idx}-${message}`}>{message}</li>
      ))}
    </ul>
  ) : null;

// A package saved with a duration outside the 30-min list still has to show its own value.
const durationOptionsFor = (minutes) =>
  DURATION_OPTIONS.some((opt) => opt.value === Number(minutes))
    ? DURATION_OPTIONS
    : [{ value: Number(minutes), label: formatDuration(minutes) || "Elegir" }, ...DURATION_OPTIONS];

const ClubBirthdaysTab = ({ value, onChange, errors = {}, courts, senas }) => {
  const patch = (partial) => onChange((prev) => ({ ...prev, ...partial }));
  const setQuestion = (key, checked) =>
    onChange((prev) => ({ ...prev, questions: { ...prev.questions, [key]: checked } }));
  const setPolicy = (partial) => onChange((prev) => ({ ...prev, policy: { ...prev.policy, ...partial } }));

  const updatePackage = (key, partial) =>
    onChange((prev) => ({
      ...prev,
      packages: prev.packages.map((p) => (p._key === key ? { ...p, ...partial } : p)),
    }));
  const removePackage = (key) =>
    onChange((prev) => ({ ...prev, packages: prev.packages.filter((p) => p._key !== key) }));

  const updateWindow = (index, partial) =>
    onChange((prev) => ({
      ...prev,
      windows: prev.windows.map((w, i) => (i === index ? { ...w, ...partial } : w)),
    }));
  const toggleWeekday = (index, weekday) =>
    onChange((prev) => ({
      ...prev,
      windows: prev.windows.map((w, i) => {
        if (i !== index) return w;
        const on = w.weekdays.includes(weekday);
        return { ...w, weekdays: on ? w.weekdays.filter((d) => d !== weekday) : [...w.weekdays, weekday] };
      }),
    }));
  const removeWindow = (index) =>
    onChange((prev) => ({ ...prev, windows: prev.windows.filter((_, i) => i !== index) }));

  const toggleCourt = (courtId) =>
    onChange((prev) => ({
      ...prev,
      courtIds: prev.courtIds.includes(courtId)
        ? prev.courtIds.filter((id) => id !== courtId)
        : [...prev.courtIds, courtId],
    }));

  const isApproval = value.mode === "approval";
  // While the permission loads the deposit stays locked too: nothing is saved half way.
  const depositLocked = senas.cargando || !senas.puedeCobrar;

  return (
    <div className="zr-bday">
      <label className="zr-bday__enable">
        <input
          type="checkbox"
          className="zr-switch"
          checked={value.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
        <span>
          <b>Ofrecer cumpleaños en la web</b>
          <span className="zr-bday__enable-hint">
            En la página del club aparece “Cumpleaños”: el cliente elige un paquete, un horario libre y lo personaliza.
          </span>
        </span>
      </label>

      <section className="zr-panel" aria-labelledby="bday-modo">
        <div className="zr-section-head">
          <h2 className="zr-section-head__title" id="bday-modo">Cómo se reservan</h2>
          <p className="zr-section-head__hint">| Vale para todos los paquetes de este club.</p>
        </div>
        <div className="zr-bday__modes" role="radiogroup" aria-labelledby="bday-modo">
          <label className={"zr-bday__mode" + (!isApproval ? " is-on" : "")}>
            <input
              type="radio"
              name="birthday-mode"
              checked={!isApproval}
              onChange={() => patch({ mode: "direct" })}
            />
            <span>
              <b>Reserva directa</b>
              <span>
                El cliente elige paquete y horario, paga la seña y queda confirmado al instante. Ideal si tus cumples son
                siempre iguales.
              </span>
            </span>
          </label>
          <label className={"zr-bday__mode" + (isApproval ? " is-on" : "")}>
            <input
              type="radio"
              name="birthday-mode"
              checked={isApproval}
              onChange={() => patch({ mode: "approval" })}
            />
            <span>
              <b>Con aprobación</b>
              <span>
                El cliente manda una solicitud con lo que quiere. Vos la aceptás, le proponés otro horario o la rechazás.
                Si la aceptás, le llega el link para pagar la seña.
              </span>
            </span>
          </label>
        </div>

        {isApproval ? (
          <>
            <div className="zr-bday__grid2">
              <div className="zr-field">
                <span className="zr-field__label" id="bday-response-label">Tenés para responder cada solicitud</span>
                <OrganizerPillSelect
                  id="bday-response"
                  labelledBy="bday-response-label"
                  value={value.responseHours}
                  onChange={(val) => patch({ responseHours: Number(val) })}
                  options={HOURS_OPTIONS}
                />
                <span className="zr-bday__help">Si no respondés a tiempo, vence y le avisamos al cliente.</span>
              </div>
              <div className="zr-field">
                <span className="zr-field__label" id="bday-payment-label">Plazo para pagar la seña una vez aceptada</span>
                <OrganizerPillSelect
                  id="bday-payment"
                  labelledBy="bday-payment-label"
                  value={value.paymentHours}
                  onChange={(val) => patch({ paymentHours: Number(val) })}
                  options={HOURS_OPTIONS}
                />
                <span className="zr-bday__help">Mientras tanto la cancha queda tomada; si no paga, se libera sola.</span>
              </div>
            </div>
            <p className="zr-notice">
              Cada solicitud nueva te llega a las notificaciones del portal ZOCO y por mail. Una solicitud no reserva la
              cancha: al aceptarla revisamos que el horario siga libre.
            </p>
          </>
        ) : (
          <p className="zr-notice">
            Mientras el cliente paga, la cancha se le guarda 10 minutos, como cualquier reserva con seña de la web.
          </p>
        )}
        <ErrorList messages={errors.modo} />
      </section>

      <section className="zr-panel" aria-labelledby="bday-paquetes">
        <div className="zr-bday__head">
          <div className="zr-section-head" style={{ marginBottom: 0 }}>
            <h2 className="zr-section-head__title" id="bday-paquetes">Paquetes</h2>
            <p className="zr-section-head__hint">
              | Lo que el cliente puede elegir. El precio es el del cumple completo, no el de la cancha por hora.
            </p>
          </div>
          <button
            type="button"
            className="zr-btn zr-btn--ghost"
            onClick={() => patch({ packages: [...value.packages, newBirthdayPackage()] })}
            disabled={value.packages.length >= MAX_PACKAGES}
          >
            + Agregar paquete
          </button>
        </div>

        {value.packages.length === 0 ? (
          <p className="zr-bday__empty">
            Todavía no cargaste paquetes. Agregá el primero para poder ofrecer cumpleaños.
          </p>
        ) : (
          <div className="zr-bday__tablewrap">
            <table className="zr-bday__table">
              <thead>
                <tr>
                  <th scope="col">PAQUETE</th>
                  <th scope="col">DURACIÓN</th>
                  <th scope="col">PRECIO ($)</th>
                  <th scope="col">INCLUYE</th>
                  <th scope="col">ACTIVO</th>
                  <th scope="col"><span className="zr-bday__sr">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {value.packages.map((pkg, idx) => {
                  const n = idx + 1;
                  return (
                    <tr key={pkg._key}>
                      <td>
                        <input
                          className="zr-input"
                          aria-label={`Nombre del paquete ${n}`}
                          value={pkg.name}
                          maxLength={60}
                          placeholder="Ej: Cumple 3 h + quincho"
                          onChange={(e) => updatePackage(pkg._key, { name: e.target.value })}
                        />
                      </td>
                      <td>
                        <OrganizerPillSelect
                          value={pkg.durationMinutes}
                          onChange={(val) => updatePackage(pkg._key, { durationMinutes: Number(val) })}
                          options={durationOptionsFor(pkg.durationMinutes)}
                        />
                      </td>
                      <td>
                        <input
                          className="zr-input"
                          type="number"
                          min="0"
                          aria-label={`Precio del paquete ${n}`}
                          value={pkg.price}
                          onChange={(e) => updatePackage(pkg._key, { price: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="zr-input"
                          aria-label={`Qué incluye el paquete ${n}`}
                          value={pkg.includes}
                          maxLength={120}
                          placeholder="Cancha de fútbol 5 y quincho"
                          onChange={(e) => updatePackage(pkg._key, { includes: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="zr-switch"
                          aria-label={`Paquete ${n} activo`}
                          checked={pkg.active}
                          onChange={(e) => updatePackage(pkg._key, { active: e.target.checked })}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="zr-pill-btn"
                          aria-label={`Quitar paquete ${n}`}
                          onClick={() => removePackage(pkg._key)}
                        >
                          Quitar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ErrorList messages={errors.paquetes} />
      </section>

      <section className="zr-panel" aria-labelledby="bday-donde">
        <div className="zr-section-head">
          <h2 className="zr-section-head__title" id="bday-donde">Dónde y cuándo</h2>
          <p className="zr-section-head__hint">| En qué canchas y en qué días y horarios se pueden pedir.</p>
        </div>

        <fieldset className="zr-bday__fieldset">
          <legend className="zr-field__label">Canchas habilitadas</legend>
          {courts.loading && courts.courts.length === 0 ? (
            <p className="zr-bday__empty">Cargando canchas...</p>
          ) : courts.error ? (
            <div className="zr-bday__retry">
              <span>{courts.error}</span>
              <button type="button" className="zr-btn zr-btn--ghost" onClick={courts.reload}>Reintentar</button>
            </div>
          ) : courts.courts.length === 0 ? (
            <p className="zr-bday__empty">
              Este club todavía no tiene canchas activas. Cargalas en la pestaña CANCHAS.
            </p>
          ) : (
            <div className="zr-bday__courts">
              {courts.courts.map((court) => {
                const on = value.courtIds.includes(court.id);
                const sport = court.sport ? ` · ${SPORT_LABELS[court.sport] || court.sport}` : "";
                return (
                  <label key={court.id} className={"zr-bday__court" + (on ? " is-on" : "")}>
                    <input type="checkbox" className="zr-check" checked={on} onChange={() => toggleCourt(court.id)} />
                    {`${court.name}${sport}`}
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        <div className="zr-bday__windows">
          <span className="zr-field__label">Días y horarios en que se pueden pedir</span>
          {value.windows.map((win, idx) => {
            const n = idx + 1;
            return (
              <div className="zr-bday__window" role="group" aria-label={`Franja ${n}`} key={idx}>
                <div className="zr-bday__chips">
                  {WEEKDAY_CHIPS.map((day) => {
                    const on = win.weekdays.includes(day.weekday);
                    return (
                      <button
                        type="button"
                        key={day.weekday}
                        className={"zr-bday__chip" + (on ? " is-on" : "")}
                        aria-pressed={on}
                        aria-label={`${day.label}, franja ${n}`}
                        onClick={() => toggleWeekday(idx, day.weekday)}
                      >
                        {day.short}
                      </button>
                    );
                  })}
                </div>
                <div className="zr-bday__times">
                  <OrganizerTimePillSelect
                    value={win.start}
                    onChange={(val) => updateWindow(idx, { start: val })}
                    ariaLabel={`Inicio de la franja ${n}`}
                    quarterHours
                    triggerClassName="zr-day-row__time"
                  />
                  <span aria-hidden="true">a</span>
                  <OrganizerTimePillSelect
                    value={win.end}
                    onChange={(val) => updateWindow(idx, { end: val })}
                    ariaLabel={`Fin de la franja ${n}`}
                    quarterHours
                    triggerClassName="zr-day-row__time"
                  />
                  <button
                    type="button"
                    className="zr-pill-btn"
                    aria-label={`Quitar franja ${n}`}
                    onClick={() => removeWindow(idx)}
                  >
                    Quitar
                  </button>
                </div>
              </div>
            );
          })}
          <div>
            <button
              type="button"
              className="zr-btn zr-btn--ghost"
              onClick={() => patch({ windows: [...value.windows, newBirthdayWindow()] })}
              disabled={value.windows.length >= MAX_WINDOWS}
            >
              + Agregar franja
            </button>
          </div>
        </div>

        {senas.cargando === false && !senas.puedeCobrar && <AvisoSenasBloqueadas />}
        <div className="zr-bday__grid3">
          <div className="zr-field">
            <label className="zr-field__label" htmlFor="bday-min-advance">Anticipación mínima (horas)</label>
            <input
              id="bday-min-advance"
              className="zr-input"
              type="number"
              min="0"
              value={value.minAdvanceHours}
              onChange={(e) => patch({ minAdvanceHours: e.target.value })}
            />
          </div>
          <div className="zr-field">
            <label className="zr-field__label" htmlFor="bday-max-advance">Hasta cuánto antes (días)</label>
            <input
              id="bday-max-advance"
              className="zr-input"
              type="number"
              min="1"
              max="365"
              value={value.maxAdvanceDays}
              onChange={(e) => patch({ maxAdvanceDays: e.target.value })}
            />
          </div>
          <div className="zr-field">
            <label className="zr-field__label" htmlFor="bday-deposit">Seña para confirmar (%)</label>
            <input
              id="bday-deposit"
              className="zr-input"
              type="number"
              min="0"
              max="100"
              value={value.depositPercent}
              disabled={depositLocked}
              onChange={(e) => patch({ depositPercent: e.target.value })}
            />
            <span className="zr-bday__help">
              Con 0 % no se cobra seña. Cobrarla requiere tus cobros validados en ZOCO Pagos.
            </span>
          </div>
        </div>
        <ErrorList messages={errors.donde} />
      </section>

      <section className="zr-panel" aria-labelledby="bday-preguntas">
        <div className="zr-section-head">
          <h2 className="zr-section-head__title" id="bday-preguntas">Qué le preguntamos al cliente</h2>
          <p className="zr-section-head__hint">| Además de su nombre, email, WhatsApp y DNI, que se piden siempre.</p>
        </div>
        <div className="zr-bday__questions">
          {QUESTIONS.map((question) => (
            <label key={question.key} className="zr-bday__question">
              <input
                type="checkbox"
                className="zr-check"
                checked={value.questions[question.key] !== false}
                onChange={(e) => setQuestion(question.key, e.target.checked)}
              />
              {question.label}
            </label>
          ))}
        </div>
      </section>

      <section className="zr-panel" aria-labelledby="bday-politica">
        <div className="zr-section-head">
          <h2 className="zr-section-head__title" id="bday-politica">Política de cancelación</h2>
          <p className="zr-section-head__hint">| El cliente la ve y la acepta antes de mandar la solicitud o pagar.</p>
        </div>
        <div className="zr-bday__refund">
          <span>Devolvemos la seña si cancela con al menos</span>
          <input
            className="zr-input zr-bday__days"
            type="number"
            min="0"
            aria-label="Días de anticipación"
            value={value.policy.refundDays}
            onChange={(e) => setPolicy({ refundDays: e.target.value })}
          />
          <span>días de anticipación</span>
        </div>
        <div className="zr-field" style={{ marginTop: 18 }}>
          <label className="zr-field__label" htmlFor="bday-policy-text">Texto que ve el cliente</label>
          <textarea
            id="bday-policy-text"
            className="zr-textarea"
            style={{ minHeight: 90 }}
            maxLength={1000}
            value={value.policy.text}
            placeholder="Si cancelás con 7 días o más, te devolvemos la seña. Con menos anticipación la seña no se devuelve."
            onChange={(e) => setPolicy({ text: e.target.value })}
          />
        </div>
        <ErrorList messages={errors.politica} />
      </section>
    </div>
  );
};

export default ClubBirthdaysTab;
